import { useMemo } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import BlockIcon from '@/shared/assets/svg/block.svg';
import { Link } from '@/shared/components/atoms/Link';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import {
  getValidatorsByAccountIdsQuery,
  paginatedPenaltiesByValidatorQuery,
} from '@/shared/graphql/explorer/authorities';
import { type PenaltyFragment } from '@/shared/graphql/generated/explorer/graphql';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import { formatWithCommas } from '@/shared/utils/numbers';

const PenaltyRow = ({
  penalty,
  showValidator,
}: {
  penalty: PenaltyFragment;
  showValidator: boolean;
}) => (
  <tr>
    {showValidator && (
      <td className="flex items-center gap-x-2  whitespace-nowrap text-12">
        <div className="flex flex-col gap-y-2">
          {penalty.validator.account.alias}
          <div className="flex items-center gap-x-1">
            <Link
              href={buildChainflipExplorerLink(penalty.validator.account.idSs58, 'validators')!}
              target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
              rel="noreferrer"
              underline
              noPropagate
              color="light"
              className="whitespace-nowrap"
            >
              {abbreviate(penalty.validator.account.idSs58, 4)}
            </Link>
            <CopyButton textToCopy={penalty.validator.account.idSs58} />
          </div>
        </div>
      </td>
    )}
    <td className="whitespace-nowrap text-14">{penalty.reason}</td>
    <td className="text-14">
      <Link
        href={buildChainflipExplorerLink(penalty.block.id.toString(), 'blocks')!}
        target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
        underline
        noPropagate
      >
        <span className="flex items-center">
          <BlockIcon className="mr-[5px] text-cf-green-1" />
          <span className="cursor-pointer underline">{formatWithCommas(penalty.block.id)}</span>
        </span>
      </Link>
    </td>
    <td className="text-right">-{penalty.amount}</td>
    <td className="whitespace-nowrap text-right text-14 text-cf-light-2">
      <AgeWithTooltip timestamp={penalty.block.timestamp} />
    </td>
  </tr>
);

export const PenaltiesTable = ({
  accountIds,
  showValidator = false,
  isLoading = false,
}: {
  accountIds: `cF${string}`[];
  showValidator?: boolean;
  isLoading?: boolean;
}): React.JSX.Element => {
  const { data: validatorsData, isLoading: validatorsLoading } = useGqlQuery(
    getValidatorsByAccountIdsQuery,
    {
      variables: { ids: accountIds },
    },
  );
  const validatorIds = validatorsData?.validators?.nodes.map((v) => v.id);

  const {
    data,
    paginationControls,
    isLoading: isPenaltiesLoading,
    error,
  } = useRemotePagination(paginatedPenaltiesByValidatorQuery, {
    variables: { ids: validatorIds },
    enabled: validatorIds !== undefined && validatorIds.length > 0,
    useCursor: true,
  });

  const penaltyRows = useMemo(
    () =>
      data?.penalties?.edges.map(({ node }) => (
        <PenaltyRow key={node.id} penalty={node} showValidator={showValidator} />
      )) ?? [],
    [data, showValidator],
  );

  if (error) throw error;
  return (
    <TableV2
      columns={[
        ...(showValidator ? ['Validator ID'] : []),
        'Offence',
        'Block',
        { name: 'Reputation', alignment: 'right' },
        { name: 'Age', alignment: 'right' },
      ]}
      rows={penaltyRows}
      isLoading={isPenaltiesLoading || validatorsLoading || isLoading}
      paginationControls={paginationControls}
    />
  );
};

export default PenaltiesTable;
